import React, { useState, useEffect, useMemo, useRef, useContext } from 'react'
import { useAsyncEffect, useRequest } from 'ahooks'
import api from 'services/api'
import { getUserInfoCache } from 'utils/user-info'
import accounting from 'accounting'
import { headers } from 'config'
import StripeCheckoutForm from 'components/stripeCheckoutForm'
import Loading from 'components/loading'
import { createPortal } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { AppContext } from 'contexts/appContext'
import router from 'next/router'

const ResultModal = (props: any): JSX.Element => {
  const { data, onConfirm } = props
  return (
    <>
      {createPortal(
        <div
          className={`fixed top-0 left-0 flex items-center justify-center w-full h-full bg-black bg-opacity-30 z-[999]`}
        >
          {data.status === 'success' ? (
            <div className="relative flex items-center flex-col w-3/4 max-w-md p-4 pt-12 bg-white rounded-2xl">
              <img
                src="/images/icon-coins-large.png"
                className="absolute w-16 h-16 -top-6 left-1/2 -ml-8"
              />
              <div className="mb-3 text-lg font-bold">
                Transaction Succeeded
              </div>
              <div className="text-lg font-bold text-[#D82626]">
                {`+ ${data.coins} coins`}
              </div>
              <div className="mb-3">{`${data.bonus} bonus`}</div>
              <div className="p-3 mb-3 bg-gray-100 rounded text-sm text-gray-400">
                If coins haven{"'"}t shown in your wallet, please <b>refresh</b>{' '}
                page
              </div>
              <div
                className="flex items-center justify-center rounded-full w-2/3 p-3 bg-gold cursor-pointer text-goldInnerText"
                onClick={() => {
                  onConfirm?.()
                }}
              >
                I‘M AWARE!
              </div>
            </div>
          ) : (
            <div className="relative flex items-center flex-col w-3/4 p-4 pt-12 bg-white rounded-2xl cursor-pointer">
              <img
                src="/images/icon-recharge-fail.png"
                className="absolute w-24 -top-6 left-1/2 -ml-12"
              />
              <div className="mb-3 text-lg font-bold">Transaction Failed</div>
              <div className="mb-3 text-sm">
                {data.message || 'Transaction Failed. Please try again'}
              </div>
              <div
                className="flex items-center justify-center rounded-full w-1/2 p-3 bg-gold"
                onClick={() => {
                  onConfirm?.()
                }}
              >
                OK
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  )
}

const Index = ({
  deveice = 'mobile',
  showClose = true,
  paymentId,
  onComplete,
  onClose,
}: {
  deveice?: string
  showClose?: boolean
  paymentId?: string
  onComplete?: (data: any) => void
  onClose?: () => void
}): JSX.Element => {
  const { setWallet } = useContext(AppContext)
  const [userProfile, setUserProfile] = useState(null)
  const [walletProfile, setWalletProfile] = useState(null)
  const [productList, setProductList] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [productIndex, setProductIndex] = useState(0)
  const [payTypeIndex, setPayTypeIndex] = useState(0)
  const [currentProduct, setCurrentProduct] = useState(null)
  const [currentPayType, setCurrentPayType] = useState(null)
  const [showProductDetailPanel, setShowProductDetailPanel] = useState(false)
  const [showStripeDropin, setShowStripeDropin] = useState(false)
  const [stripeCheckout, setStripeCheckout] = useState(null)
  const [showRules, setShowRules] = useState(false)
  const [payResult, setPayResult] = useState({
    show: false,
    status: '',
    message: '',
  })
  const payResultModalRoot = useRef(null)

  const formatMoney = (value) => {
    return accounting.formatMoney(Math.round(value), '', 0)
  }

  const checkCommonOrder = useRequest(
    () => {
      return api.pay.checkCommonOrder({ paymentId })
    },
    {
      manual: true,
      pollingInterval: 1000,
      pollingWhenHidden: true,
    }
  )

  const getWalletProfile = () => {
    api.wallet.getWalletProfile().then((response: any) => {
      response.balance = parseInt(response.balance, 10)
      response.bonus = parseInt(response.bonus, 10)
      setWalletProfile(response)
      setWallet(response)
    })
  }

  const getProductList = async () => {
    return api.pay
      .getProductList({
        sourceType: 1,
        rechargeType: 1,
      })
      .then((response) => {
        setProductList(response.products)
        setPaymentMethods(response.paymentMethods)
        setCurrentProduct(response.products[0])
        setCurrentPayType(response.paymentMethods[0])
      })
  }

  const close = () => {
    setShowStripeDropin(false)
    setStripeCheckout(null)
    onClose()
  }

  useEffect(() => {
    setUserProfile(getUserInfoCache())
    const lastPayInfo = localStorage.getItem('LASTPAYINFO')
      ? JSON.parse(localStorage.getItem('LASTPAYINFO'))
      : null
    setProductIndex(lastPayInfo?.productIndex || productIndex)
    setPayTypeIndex(lastPayInfo?.payTypeIndex || payTypeIndex)
    return () => {
      setShowStripeDropin(false)
      setStripeCheckout(null)
    }
  }, [])

  useAsyncEffect(async () => {
    if (userProfile) {
      getWalletProfile()
      Loading.show()
      await getProductList()
      Loading.hide()
    }
  }, [userProfile])

  useEffect(() => {
    if (paymentId) {
      checkCommonOrder.runAsync()
    }
  }, [paymentId])

  useMemo(() => {
    setCurrentProduct(productList[productIndex])
    setCurrentPayType(paymentMethods[payTypeIndex])
  }, [productIndex, payTypeIndex, productList, paymentMethods])

  useEffect(() => {
    if (checkCommonOrder.data) {
      const { status, payFailReason } = checkCommonOrder.data
      if (status === 2) {
        checkCommonOrder.cancel()
        setPayResult({
          show: true,
          status: 'success',
          message: '',
        })
        Loading.hide()
      } else if (status === 0 && payFailReason) {
        checkCommonOrder.cancel()
        setPayResult({
          show: true,
          status: 'fail',
          message: payFailReason,
        })
        Loading.hide()
      } else if (status >= 3) {
        checkCommonOrder.cancel()
        Loading.hide()
      }
    }
  }, [checkCommonOrder.data])

  useEffect(() => {
    if (payResult.show && currentProduct) {
      const model = document.createElement('div')
      model.id = 'J_PAYRESULTMODAL_PORTAL'
      document.body.appendChild(model)
      payResultModalRoot.current = createRoot(model)
      payResultModalRoot.current.render(
        <ResultModal
          data={{
            ...payResult,
            coins: formatMoney(currentProduct.coins),
            bonus: formatMoney(
              (currentProduct.bonus > 0 ? currentProduct.bonus : 0) +
                (currentPayType.giftRatio > 0
                  ? currentProduct.coins * currentPayType.giftRatio
                  : 0)
            ),
          }}
          onConfirm={() => {
            getWalletProfile()
            setPayResult({
              show: false,
              status: '',
              message: '',
            })
            localStorage.removeItem('LASTPAYINFO')
            // clear paymentId
            const currentUrl = location.href
            const urlObject = new URL(currentUrl)
            urlObject.searchParams.delete('paymentId')
            const newUrl = urlObject.toString()
            history.replaceState({}, '', newUrl)
            setTimeout(() => {
              onComplete?.(payResult)
            }, 100)
          }}
        />
      )
    } else {
      payResultModalRoot?.current?.unmount()
    }
    return () => {
      if (document.querySelector('#J_PAYRESULTMODAL_PORTAL')) {
        document.body.removeChild(
          document.querySelector('#J_PAYRESULTMODAL_PORTAL')
        )
      }
    }
  }, [payResult, currentProduct])

  const onPayClick = async () => {
    if (!getUserInfoCache()) {
      router.push(`/login?returnUrl=${encodeURIComponent(location.pathname)}`)
      return false
    }

    const sensorsPublicProperty = {}

    const payTypeForBrowserMap = {
      adyengooglepay: 'adyenGpay',
      adyenapplepay: 'adyenApplePay',
      stripegooglepay: 'stripeGpay',
      stripeapplepay: 'stripeApplePay',
    }

    if (
      currentPayType.name === 'adyengooglepay' ||
      currentPayType.name === 'adyenapplepay' ||
      currentPayType.name === 'stripegooglepay' ||
      currentPayType.name === 'stripeapplepay'
    ) {
      const url = `https://${
        location.host.split('.')[0] === 'h5' ? 'h5' : `test-h5`
      }.xbooks.app/pay_in_browser.html?token=${userProfile.token}&platform=${
        headers['X-OS']
      }&packageName=${headers['X-AppIdentifier']}&appName=${
        headers['X-AppName']
      }&appVersion=${headers['X-AppVersion']}&preProduction=${
        headers['X-Pre-Production']
      }&productId=${currentProduct.productId}&paySourceId=${
        currentProduct.platformId
      }&payType=${
        payTypeForBrowserMap[currentPayType.name]
      }&productType=coins&price=${currentProduct.price}&coins=${
        currentProduct.coins
      }&bonus=${
        (currentProduct.bonus > 0 ? currentProduct.bonus : 0) +
        (currentPayType.giftRatio > 0
          ? currentProduct.coins * currentPayType.giftRatio
          : 0)
      }&productBonusPercent=${currentProduct.bonusPercent.replace(
        '%',
        ''
      )}&prouctBonus=${
        currentProduct.bonus > 0 ? currentProduct.bonus : 0
      }&payTypePerCent=${currentPayType.giftRatio}&payTypeBonus=${
        currentPayType.giftRatio > 0
          ? currentProduct.coins * currentPayType.giftRatio
          : 0
      }&sensors=${encodeURIComponent('{}')}`
      location.href = url
      return false
    }

    const orderParams = {
      stripe: {
        channel: 'h5-stripe',
        paymentType: 7,
      },
      stripegooglepay: {
        channel: 'h5-stripe-google',
        paymentType: 8,
      },
      stripeapplepay: {
        channel: 'h5-stripe-apple',
        paymentType: 9,
      },
      adyen: {
        channel: 'h5-adyen',
        paymentType: 4,
      },
      adyengooglepay: {
        channel: 'h5-adyen-google',
        paymentType: 5,
      },
      adyenapplepay: {
        channel: 'h5-adyen-apple',
        paymentType: 6,
      },
      paypal: {
        channel: 'h5-paypal',
        paymentType: 3,
        callbackState: headers['X-AppName'] === 'AnyStories' ? 1 : 5,
        returnUrl: location.href,
        cancelUrl: location.href,
      },
    }

    Loading.show()

    api.pay
      .createCommonOrder({
        productId: currentProduct.productId,
        sensorsPublicProperty,
        platform: headers['X-OS'],
        paymentMethodStrategyId: currentPayType.paymentMethodStrategyId,
        ...orderParams[currentPayType.name],
      })
      .then((data) => {
        localStorage.setItem(
          'LASTPAYINFO',
          JSON.stringify({ productIndex, payTypeIndex })
        )
        if (currentPayType.name === 'paypal' && data?.linkUrl) {
          Loading.hide()
          localStorage.setItem(
            'LASTPAYINFO',
            JSON.stringify({ productIndex, payTypeIndex })
          )
          window.location.href = data.linkUrl
        } else {
          setStripeCheckout(data)
          setShowStripeDropin(true)
          Loading.hide()
        }
      })
      .catch((error) => {
        Loading.hide()
        alert(error)
      })
  }

  return (
    <>
      <div
        className={`relative
         ${deveice === 'pc' ? 'h-full pt-4' : ''} 
         ${
           (stripeCheckout && showStripeDropin) || deveice === 'pc'
             ? 'max-h-full'
             : 'max-h-[60%]'
         }`}
      >
        {showClose && productList.length > 0 && (
          <div className="flex justify-between p-4">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => {
                close()
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <div
              className="flex items-center text-gray-500 cursor-pointer"
              onClick={() => {
                setShowRules(true)
              }}
            >
              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>
        )}
        <div className="h-full overflow-y-scroll">
          {productList.length > 0 ? (
            <div className={`pb-28 ${showStripeDropin ? 'hidden' : ''}`}>
              <div className="px-4 flex items-center text-xs">
                <span>Balance:</span>
                <img src="/images/icon-coins.png" alt="" className="w-6 h-6" />
                <span className="font-medium text-base">
                  {formatMoney(
                    walletProfile
                      ? walletProfile.balance + walletProfile.bonus
                      : 0
                  )}
                </span>
                {walletProfile && walletProfile?.bonus > 0 && (
                  <span className="text-gray-400 pl-1">
                    ({formatMoney(walletProfile.bonus)} Bonus Included)
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 p-4 gap-4">
                {productList.map((item, index) => {
                  return (
                    <div
                      className={`relative flex items-center justify-center flex-col h-28 py-5 pb-11  border-solid rounded-lg shadow cursor-pointer ${
                        productIndex === index
                          ? 'border-gold border-2'
                          : 'border-gray-400 border-opacity-30 border-[1.5px]'
                      }`}
                      key={item.productId}
                      onClick={() => {
                        setProductIndex(index)
                      }}
                    >
                      <div className="flex items-center">
                        <img
                          src="/images/icon-coins.png"
                          alt=""
                          className="w-6 h-6"
                        />
                        <span className="font-bold text-xl">
                          {formatMoney(item.coins)}
                        </span>
                      </div>
                      {+item.bonus > 0 ? (
                        <div className="flex items-center">
                          <span className="text-gray-200 text-xs word-break">
                            +{formatMoney(item.bonus)} Bonus
                          </span>
                          <span className="flex items-center ml-1 py-0.5 px-2 bg-[#FE6E00] rounded-full text-xs text-white">
                            <svg
                              className="h-3 w-3 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z"
                                clipRule="evenodd"
                              />
                              <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
                            </svg>
                            {item.bonusPercent}
                          </span>
                        </div>
                      ) : null}
                      {item.marketTag ? (
                        <div className="absolute -top-[1.5px] -left-[1.5px] px-2 py-0.5 rounded-tl-lg rounded-tr-sm rounded-br-xl bg-gray-200 text-xs text-goldInnerText">
                          {item.marketTag}
                        </div>
                      ) : null}
                      <div
                        className={`absolute bottom-0 left-0 flex items-center justify-center w-full h-8 rounded-b text-center text-gray-500 ${
                          productIndex === index
                            ? 'bg-gold bg-opacity-50'
                            : 'bg-gray-100'
                        }`}
                      >
                        US$ {item.price}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="px-4">
                <div className="font-bold">Payment Method</div>
                <div className="flex items-center py-2 overscroll-contain overflow-x-auto">
                  {paymentMethods.map((item, index) => {
                    return (
                      <div
                        className={`relative flex flex-none items-center justify-center w-24 h-14 mr-4 last:mr-0 border-solid rounded-lg shadow cursor-pointer  ${
                          payTypeIndex === index
                            ? 'border-2 border-gold bg-right-top bg-checked bg-no-repeat'
                            : 'border-[1.5px] border-gray-400 border-opacity-30'
                        }`}
                        onClick={() => {
                          setPayTypeIndex(index)
                        }}
                        key={index}
                      >
                        <img
                          src={`/images/icon-${item.name}.png`}
                          className="w-2/3"
                          alt={item.name}
                        />
                        {item.giftRatioDescribe && (
                          <span className="absolute -top-[2px] -left-[2px] flex items-center px-2 py-[0.5px] bg-orange rounded-tl-lg rounded-tr-sm rounded-br-lg text-xs text-white">
                            <svg
                              className="h-3 w-3 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z"
                                clipRule="evenodd"
                              />
                              <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
                            </svg>
                            {item.giftRatioDescribe}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
              {productList.length > 0 && currentProduct && currentPayType && (
                <div className="absolute left-0 bottom-0 px-4 flex items-center justify-between w-full h-16 bg-white border-t border-gray-400 border-opacity-30 z-[104]">
                  <div
                    className="flex flex-col cursor-pointer"
                    onClick={() => {
                      setShowProductDetailPanel(!showProductDetailPanel)
                    }}
                  >
                    <div className="flex items-center">
                      <span className="font-medium text-base">Total:</span>
                      <img
                        src="/images/icon-coins.png"
                        alt=""
                        className="w-6 h-6"
                      />
                      <span className="font-border text-xl font-bold">
                        {formatMoney(
                          currentProduct.coins +
                            (currentProduct.bonus > 0
                              ? currentProduct.bonus
                              : 0) +
                            (currentPayType.giftRatio > 0
                              ? currentProduct.coins * currentPayType.giftRatio
                              : 0) +
                            (currentProduct.dailyBonus
                              ? currentProduct.dailyBonus * 31
                              : 0)
                        )}
                      </span>
                    </div>
                    {currentProduct.bonus > 0 ? (
                      <div className="flex items-center text-xs">
                        <span className="text-orange mr-2 ">
                          {formatMoney(
                            (currentProduct.bonus > 0
                              ? currentProduct.bonus
                              : 0) +
                              (currentPayType.giftRatio > 0
                                ? currentProduct.coins *
                                  currentPayType.giftRatio
                                : 0) +
                              (currentProduct.dailyBonus
                                ? currentProduct.dailyBonus * 31
                                : 0)
                          )}{' '}
                          Bonus
                        </span>
                        <span className="text-gray-400">includes</span>
                        <svg
                          className={`h-4 w-4 text-gray-400 transition transform ${
                            showProductDetailPanel ? 'rotate-180' : ''
                          }`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    ) : null}
                  </div>
                  <div
                    className="px-6 py-3 flex items-center font-bold bg-gold rounded-full cursor-pointer text-goldInnerText"
                    onClick={() => {
                      onPayClick()
                    }}
                  >
                    Pay ${productList[productIndex]['price']}
                  </div>
                </div>
              )}
              {showProductDetailPanel ? (
                <div
                  className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-30 z-[102]"
                  onClick={() => {
                    setShowProductDetailPanel(false)
                  }}
                ></div>
              ) : null}
              <div
                className="absolute left-0 bottom-0 w-full p-4 pb-20 bg-white z-[103] transition-all	duration-200 ease-linear"
                style={{
                  transform: showProductDetailPanel
                    ? 'translateY(0%)'
                    : 'translateY(100%)',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 absolute top-4 left-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  onClick={() => {
                    setShowProductDetailPanel(false)
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <div className="font-medium text-center mb-2">Details</div>
                <div className="flex justify-between py-2 text-sm">
                  <div>
                    <span className="font-medium">Item Selected</span>
                  </div>
                  <div>
                    <span className="font-medium mr-2">
                      {formatMoney(currentProduct.coins)}
                    </span>
                    <span className="text-gray-400">Coins</span>
                  </div>
                </div>
                {currentProduct.bonus > 0 && (
                  <div className="flex justify-between py-2 text-sm">
                    <div>
                      <span className="font-medium mr-1">Top-up Bonus</span>
                      <span className="text-gray-400">
                        {currentProduct.bonusPercent
                          ? `(+${currentProduct.bonusPercent})`
                          : ''}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-orange mr-2">
                        +{formatMoney(currentProduct.bonus)}
                      </span>
                      <span className="font-normal text-gray-400">Bonus</span>
                    </div>
                  </div>
                )}
                {currentPayType?.giftRatio > 0 && (
                  <div className="flex justify-between py-2 text-sm">
                    <div>
                      <span className="font-medium mr-1">Payment Bonus</span>
                      <span className="text-gray-400">
                        (+
                        {parseInt(currentPayType.giftRatio * 100 + '', 10) +
                          '%'}
                        )
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-orange mr-2">
                        +
                        {formatMoney(
                          currentProduct.coins * currentPayType.giftRatio
                        )}
                      </span>
                      <span className="font-normal text-gray-400">Bonus</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
          {stripeCheckout && showStripeDropin ? (
            <div className="p-4">
              <div className="pb-3 mb-6 border-b-4 border-double border-gray-400">
                <div className="flex items-center justify-between py-2 text-gray-200">
                  <div>Item Selected</div>
                  <div>
                    <span className="text-gray-200">
                      {formatMoney(currentProduct.coins)} Coins
                    </span>
                  </div>
                </div>
                {currentProduct.bonus > 0 ? (
                  <div className="flex items-center justify-between py-2 text-gray-200">
                    <div>
                      Top-up Bonus
                      <span className="text-gray-400">
                        {currentProduct.bonusPercent
                          ? `(+${currentProduct.bonusPercent})`
                          : ''}
                      </span>
                    </div>
                    <div>
                      <span className="text-orange">
                        +{formatMoney(currentProduct.bonus)}{' '}
                      </span>
                      <span>Bonus</span>
                    </div>
                  </div>
                ) : null}
                {currentPayType.giftRatio > 0 ? (
                  <div className="flex items-center justify-between py-2 text-gray-200">
                    <div>
                      Payment Bonus
                      <span className="text-gray-400">
                        (+
                        {parseInt(currentPayType.giftRatio * 100 + '', 10) +
                          '%'}
                        )
                      </span>
                    </div>
                    <div>
                      <span className="text-orange">
                        +
                        {formatMoney(
                          currentProduct.coins * currentPayType.giftRatio
                        )}{' '}
                      </span>
                      <span>Bonus</span>
                    </div>
                  </div>
                ) : null}
                <div className="flex items-center justify-between pt-2 mt-2 text-gray-200 font-bold">
                  <span>TOTAL</span>
                  <span className="text-[#ff4840]">
                    US$ {currentProduct.price}
                  </span>
                </div>
              </div>
              <StripeCheckoutForm
                stripeCheckout={stripeCheckout}
                onFail={(data: any) => {
                  setPayResult({
                    show: true,
                    status: 'fail',
                    message: data?.error?.message || 'recharge failed',
                  })
                }}
              />
              <div
                className="py-4 text-blue text-sm cursor-pointer"
                onClick={() => {
                  setShowStripeDropin(false)
                  setStripeCheckout(null)
                }}
              >
                {'<'} View All Items
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {showRules && (
        <div
          className={`fixed top-0 left-0 w-full h-full bg-black bg-opacity-10 z-[501]`}
        >
          <div
            className="absolute top-0 left-0 w-full h-full z-10 cursor-pointer "
            onClick={() => {
              setShowRules(false)
            }}
          ></div>
          <div className="absolute w-2/3 h-64 pt-8 z-20 rounded-t-xl bg-white lg:w-[388px] left-1/2 top-1/2  transform -translate-x-1/2 -translate-y-1/2 rounded-b-2xl">
            <div
              className="absolute right-3 top-3 flex items-center cursor-pointer"
              onClick={() => {
                setShowRules(false)
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <div className="p-4 max-h-full overflow-y-auto">
              <p className="text-gray-500 leading-tight mb-4">
                1、By tapping Purchase your payment will be charged to your
                Store account, and vour subscription will automatically renew
                for the same package length at the same price until you cancel
                in settings in the store at least 24 hours prior to the end of
                the current period. By tapping Purchase, you agree to our
                COINSPACK Terms.
              </p>
              <p className="text-gray-500 leading-tight mb-4">
                2. 3-day free trial are currently limited to the first time.
              </p>
              <p className="text-gray-500 leading-tight mb-4">
                3. Bonus is used first when unlocking chapters and it has a
                31-day valid period. Please make sure to use it before
                expiration.
              </p>
              <p className="text-gray-500 leading-tight mb-4">
                4. Coins purchased can only be used on the purchased device.
              </p>
              <p className="text-gray-500 leading-tight mb-4">
                5. Please refer to the actual amount paid as purchase stores has
                different taxes and fees in different countires.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Index
